import * as api from '../../utils/api/communicationOverviewApi';
import * as documentsApi from '../../utils/api/documentsApi';
import * as policyRimBusinessLogic from '../../../business/logic/policy-business-logic-rim';
import * as policySvsBusinessLogic from '../../../business/logic/policy-business-logic-svs';
import * as rimActions from '../rim/actions';
import * as rimApi from '../../utils/api/rimsApi';
import * as selectors from './selectors';
import * as svsActions from '../svs/actions';
import * as svsApi from '../../utils/api/svsApi';

import { BusinessResult, BusinessResultType } from '../../../business/models/policy-business.model';
import { SelectEffect, all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import {
  askedAmountModalOpen,
  deleteItems,
  deleteItemsFail,
  generateRimReports,
  generateRimReportsFail,
  generateRimReportsSuccess,
  generateSvsReports,
  generateSvsReportsFail,
  generateSvsReportsSuccess,
  getAskedAmount,
  getAskedAmountFail,
  getAskedAmountSuccess,
  getDocumentList,
  getDocumentListFail,
  getDocumentListSuccess,
  getHonoraryNoteList,
  getHonoraryNoteListFail,
  getHonoraryNoteListSuccess,
  getRimList,
  getRimListFail,
  getRimListSuccess,
  getSvsList,
  getSvsListFail,
  getSvsListSuccess,
  rimStatusChange,
  rimStatusChangeFail,
  rimStatusChangeSucess,
  sendItems,
  sendItemsFail,
  sendItemsWithWarning,
  setAskedAmount,
  setAskedAmountFail,
  setAskedAmountSuccess,
  setNonValidRimBusinessList,
  setNonValidRimList,
  setNonValidSvsBusinessList,
  setNonValidSvsList,
  validateRimList,
  validateSvsList,
  deleteOverviewModalClose
} from './actions';
import { emptySaga, withConfirmation, withConfirmationTranslated } from '../../shared/sagas';
import { mapRimStateToPolicyBusiness, mapSvsStateToPolicyBusiness } from '../../shared/selectors';

import { ApplicationState } from '../..';
import { OverviewActionTypes } from './types';
import { RimReportRequest } from '../../../business/models/dto/rim/rim-report.request.dto';
import { SvsReportRequest } from '../../../business/models/dto/svs/svs.dto';
import { fetchCommentOrReport } from '../comment-or-report/actions';
import { fetchExpertiseAttachments } from '../expertise-attachment/actions';
import { fetchImages } from '../images/actions';
import { fetchOtherAttachments } from '../other-attachments/actions';
import { getI18n } from '../../utils/i18n/strings.manager';
import { toggleIsUIDisabled } from '../../shared/actions';
import { SagaIterator } from 'redux-saga';
import { fetchHonoraryNotes } from '../honorary-note/actions';

export function selectState<T>(selector: (s: ApplicationState) => T): SelectEffect {
  return select(selector);
}

function* handleValidateSvsForSend(action: ReturnType<typeof validateSvsList>): SagaIterator {
  const nonValidIds: number[] = [];
  const nonValidResultList: BusinessResult[] = [];
  for (let i = 0; i <= action.payload.length - 1; i++) {
    const policyBusiness = yield select(mapSvsStateToPolicyBusiness, action.payload[i]);
    const validationResult = policySvsBusinessLogic.businessSvsValidateForSend(policyBusiness);
    if (validationResult.resultType !== BusinessResultType.ok) {
      nonValidIds.push(action.payload[i]);
      nonValidResultList.push(validationResult);
    }
  }

  yield put(setNonValidSvsList(nonValidIds));
  yield put(setNonValidSvsBusinessList(nonValidResultList));
}

function* handleValidateRimForSend(action: ReturnType<typeof validateRimList>): SagaIterator {
  const nonValidIds: number[] = [];
  const nonValidResultList: BusinessResult[] = [];

  for (let i = 0; i <= action.payload.length - 1; i++) {
    const policyBusiness = yield select(mapRimStateToPolicyBusiness, action.payload[i]);
    const validationResult = policyRimBusinessLogic.rimValidateForSend(policyBusiness);
    if (validationResult.resultType !== BusinessResultType.ok) {
      nonValidIds.push(action.payload[i]);
      nonValidResultList.push(validationResult);

      yield put(setNonValidRimList(nonValidIds));
      yield put(setNonValidRimBusinessList(nonValidResultList));
    }
  }
}

function* handleSendItemsWithWarning(action: ReturnType<typeof sendItemsWithWarning>): SagaIterator {
    const selectedSvsIds: number[] = yield select(selectors.selectedSvsIds);
    const selectedRimIds: number[] = yield select(selectors.selectedRimIds);
    const askedAmount: number = yield select(selectors.askedAmount);
    const caseCode: string = action.payload;

    if ((selectedSvsIds.length > 0 || selectedRimIds.length > 0) && !(askedAmount > 0)) {
      yield put(askedAmountModalOpen());
    } else {
      yield put(sendItems(caseCode));
    }
}

function* handleSendItems(action: ReturnType<typeof sendItems>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const selectedSvsIds: number[] = yield select(selectors.selectedSvsIds);
    const selectedRimIds: number[] = yield select(selectors.selectedRimIds);
    const nonValidSvsIds: number[] = yield select(selectors.nonValidSvsIds);
    const nonValidRimIds: number[] = yield select(selectors.nonValidRimIds);
    const selectedImageIds: number[] = yield select(selectors.selectedImageIds);
    const selectedOtherAttachmentIds: number[] = yield select(selectors.selectedOtherAttachmentIds);
    const selectedFreeTextIds: number[] = yield select(selectors.selectedFreeTextIds);
    const selectedReportIds: number[] = yield select(selectors.selectedReportIds);
    const selectedHonoraryNoteIds: number[] = yield select(selectors.selectedHonoraryNoteIds);

    const validSelectedSvsIds: number[] = selectedSvsIds.filter(id => !nonValidSvsIds.includes(id));
    const validSelectedRimIds: number[] = selectedRimIds.filter(id => !nonValidRimIds.includes(id));
    const nonValidSelectedSvsIds: number[] = selectedSvsIds.filter(id => nonValidSvsIds.includes(id));
    const nonValidSelectedRimIds: number[] = selectedRimIds.filter(id => nonValidRimIds.includes(id));
    const nonValidSelectedSvsBusinessList: BusinessResult[] = yield select(selectors.svsNonValidBusinessList);
    const nonValidSelectedRimBusinessList: BusinessResult[] = yield select(selectors.rimNonValidBusinessList);


    const caseCode: string = action.payload;
    const instructionId: number = yield select(selectors.selectedCaseInstructionId);

    const localization = getI18n();

    let messages: string[] = [];
    if (nonValidSelectedSvsIds.length > 0) {
      nonValidSelectedSvsIds.forEach((id) => {
        messages.push(localization.formatString('overview.svs.id', { 'svsId': id.toString() }) as string);

        nonValidSelectedSvsBusinessList.filter(item => item.data && item.data.Svs.id === id).forEach(businessResult => {
          messages = messages.concat(businessResult.messages.map(msg => localization.get(msg)));
        });

        messages.push(''); // section separator
      });
    }
    if (nonValidSelectedRimIds.length > 0) {
      // messages.push('validation.rim.list.not.sent.cause.invalid');
      nonValidSelectedRimIds.forEach((id) => {
        messages.push(localization.formatString('overview.rim.id', { 'rimId': id.toString() }) as string);

        nonValidSelectedRimBusinessList.filter(item => item.data && item.data.Rim.id === id).forEach(businessResult => {
          messages = messages.concat(businessResult.messages.map(msg => localization.get(msg)));
        });

        messages.push(''); // section separator
      });
    }

    // remove last separator line
    if (messages[messages.length - 1] === '') {
      messages.pop();
    }

    const reportsToGenerateList = [];

    const rimReportRequest = new RimReportRequest();
    rimReportRequest.caseCode = caseCode;
    rimReportRequest.rimHeaderIds = validSelectedRimIds;

    const svsReportRequest = new SvsReportRequest();
    svsReportRequest.caseCode = caseCode;
    svsReportRequest.svsHeaderIds = validSelectedSvsIds;

    let rimReportsToBeGenerated = false;
    let svsReportsToBeGenerated = false;
    if (validSelectedRimIds.length > 0) { rimReportsToBeGenerated = true; reportsToGenerateList.push(call(rimApi.generateReports, rimReportRequest)); }
    if (validSelectedSvsIds.length > 0) { svsReportsToBeGenerated = true; reportsToGenerateList.push(call(svsApi.generateReports, svsReportRequest)); }

    const generatedReports = yield all(reportsToGenerateList);

    let reportsRIMGenerated = false;
    let reportsSVSGenerated = false;

    if (rimReportsToBeGenerated) {
      if (!generatedReports[0]['data']) {
        yield put(generateRimReportsFail(generatedReports[0].response));

        yield call(withConfirmation, ['sending.items.generating.rim.reports.failed'], '', '', function* (): SagaIterator {
          yield call(emptySaga);
        });
      } else {
        if (generatedReports[0]['data'][0]['amazonKey'] === '') {
          yield put(generateRimReportsFail(generatedReports[0].response));

          yield call(withConfirmation, ['sending.items.generating.rim.reports.failed'], '', '', function* (): SagaIterator {
            yield call(emptySaga);
          });
        } else {
          yield put(generateRimReportsSuccess(generatedReports[0].data));
          reportsRIMGenerated = true;
        }
      }
    }

    if (svsReportsToBeGenerated) {
      const svsGeneratedReport = rimReportsToBeGenerated ? generatedReports[1] : generatedReports[0];
      if (!svsGeneratedReport['data']) {
        yield put(generateSvsReportsFail(svsGeneratedReport.response));

        yield call(withConfirmation, ['sending.items.generating.svs.reports.failed'], '', '', function* (): SagaIterator {
          yield call(emptySaga);
        });
      } else {
        if (generatedReports[0]['data'][0]['amazonKey'] === '') {
          yield put(generateSvsReportsFail(svsGeneratedReport.response));

        yield call(withConfirmation, ['sending.items.generating.svs.reports.failed'], '', '', function* (): SagaIterator {
          yield call(emptySaga);
        });
        } else {
          yield put(generateSvsReportsSuccess(svsGeneratedReport.data));
          reportsSVSGenerated = true;
        }
      }
    }

    const apiToCallList = [];    
    if (reportsRIMGenerated && validSelectedRimIds.length > 0) { apiToCallList.push(call(api.sendRims, validSelectedRimIds.join(','))); }
    if (reportsSVSGenerated && validSelectedSvsIds.length > 0) { apiToCallList.push(call(api.sendSvs, validSelectedSvsIds.join(','))); }

    if (selectedImageIds.length > 0) { apiToCallList.push(call(api.sendImages, selectedImageIds.join(','), instructionId)); }
    if (selectedOtherAttachmentIds.length > 0) { apiToCallList.push(call(api.sendOtherAttachments, selectedOtherAttachmentIds.join(','), instructionId)); }
    if (selectedFreeTextIds.length > 0) { apiToCallList.push(call(api.sendComments, selectedFreeTextIds.join(','))); }
    if (selectedReportIds.length > 0) { apiToCallList.push(call(api.sendReports, selectedReportIds.join(','), instructionId)); }
    if (selectedHonoraryNoteIds.length > 0) { apiToCallList.push(call(api.sendHonoraryNotes, selectedHonoraryNoteIds.join(','))); }

    const totalResult = yield all(apiToCallList);

    if (totalResult.includes((result: any) => !result.data)) {
      yield put(sendItemsFail(totalResult));

      yield call(withConfirmation, ['sending.items.failed'], '', '', function* (): SagaIterator {
        yield call(emptySaga);
      });
    } else {
      yield put(getRimList(instructionId));
      yield put(getSvsList(instructionId));
      yield put(getDocumentList(instructionId));
      yield put(getHonoraryNoteList(instructionId));
      // re load rim and svs tabs also
      if (validSelectedRimIds.length > 0) {
        yield put(rimActions.resetDetailsRimState());
        yield put(rimActions.fetchRimList(instructionId));
      }
      if (validSelectedSvsIds.length > 0) {
        yield put(svsActions.fetchSvsList(instructionId));
      }
      yield put(fetchExpertiseAttachments(instructionId));
      if (selectedImageIds.length > 0) { yield (put(fetchImages(instructionId))); }
      if (selectedOtherAttachmentIds.length > 0) { yield put(fetchOtherAttachments(instructionId)); }
      if (selectedFreeTextIds.length > 0 || selectedReportIds.length > 0) { yield (put(fetchCommentOrReport(instructionId))); }

      if (messages.length > 0) { 
        yield call(withConfirmationTranslated, messages, '', '', function* (): SagaIterator {
          yield call(emptySaga);
        });
      }
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(sendItemsFail(err));

      yield call(withConfirmation, ['sending.items.failed'], '', '', function* (): SagaIterator {
        yield call(emptySaga);
      });
    } else {
      yield put(sendItemsFail(err));

      yield call(withConfirmation, ['sending.items.failed'], '', '', function* (): SagaIterator {
        yield call(emptySaga);
      });
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleDeleteItems(action: ReturnType<typeof deleteItems>): SagaIterator {
  try {
    const selectedRimIds = yield select(selectors.selectedRimIds);
    const selectedSvsIds = yield select(selectors.selectedSvsIds);
    const documentToDeleteRequests = yield select(selectors.selectedDocumentRequestsForDelete);
    const freeTextToDeleteRequest = yield select(selectors.selectedFreeTextRequestsForDelete);
    const reportToDeleteRequest = yield select(selectors.selectedReportRequestsForDelete);
    const totalDocumentsToDeleteRequest = documentToDeleteRequests.concat(freeTextToDeleteRequest).concat(reportToDeleteRequest);
    const instructionId: number = yield select(selectors.selectedCaseInstructionId);
    const honoraryNoteIds = yield select(selectors.selectedHonoraryNoteIds);

    const apiToCallList = [];
    if (selectedRimIds.length > 0) { apiToCallList.push(call(api.deleteRims, selectedRimIds)); }
    if (selectedSvsIds.length > 0) { apiToCallList.push(call(api.deleteSvs, selectedSvsIds)); }
    if (totalDocumentsToDeleteRequest.length > 0) { apiToCallList.push(call(documentsApi.deleteDocuments, totalDocumentsToDeleteRequest)); }
    if (honoraryNoteIds.length > 0) { apiToCallList.push(call(api.deleteHonoraryNotes, honoraryNoteIds)); }

    const totalResult = yield all(apiToCallList);

    if (totalResult.includes((result: any) => !result.data)) {
      yield put(deleteItemsFail());
    } else {
      yield put(getRimList(instructionId));
      yield put(getSvsList(instructionId));
      yield put(getDocumentList(instructionId));
      // re load rim and svs tabs also
      if (selectedRimIds.length > 0) {
        yield put(rimActions.resetDetailsRimState());
        yield put(rimActions.fetchRimList(instructionId));
      }
      if (selectedSvsIds.length > 0) {
        yield put(svsActions.fetchSvsList(instructionId));
      }
      yield put(fetchExpertiseAttachments(instructionId));
      if (documentToDeleteRequests.length > 0) {
        yield (put(fetchImages(instructionId)));
        yield put(fetchOtherAttachments(instructionId));
      }
      if (freeTextToDeleteRequest.length > 0 || reportToDeleteRequest.length > 0) { yield (put(fetchCommentOrReport(instructionId))); }
      if (honoraryNoteIds.length > 0) { yield (put(fetchHonoraryNotes(instructionId))); }
    }
    yield put(deleteOverviewModalClose());
  } catch (err) {
    if (err instanceof Error) {
      // yield put(sendItemsFail(`${err.message}\n${err.name}\n${err.stack}`));
      yield put(deleteItemsFail());
    } else {
      // yield put(sendItemsFail('An unknown error occured.'));
      yield put(deleteItemsFail());
    }
  }
}

function* handleGetRimList(action: ReturnType<typeof getRimList>): SagaIterator {
  try {
    const result = yield call(api.getRims, action.payload);

    if (!result.data) {
      yield put(getRimListFail());
    } else {
      yield put(getRimListSuccess(result.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(getRimListFail());
    } else {
      yield put(getRimListFail());
    }
  }
}

function* handleGetSvsList(action: ReturnType<typeof getSvsList>): SagaIterator {
  try {
    const result = yield call(api.getSvs, action.payload);

    if (!result.data) {
      yield put(getSvsListFail());
    } else {
      yield put(getSvsListSuccess(result.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(getRimListFail());
    } else {
      yield put(getRimListFail());
    }
  }
}

function* handleGetHonoraryNoteList(action: ReturnType<typeof getHonoraryNoteList>): SagaIterator {
  try {
    const result = yield call(api.getHonoraryNotes, action.payload);

    if (!result.data) {
      yield put(getHonoraryNoteListFail());
    } else {
      yield put(getHonoraryNoteListSuccess(result.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(getHonoraryNoteListFail());
    } else {
      yield put(getHonoraryNoteListFail());
    }
  }
}

function* handleGetDocumentList(action: ReturnType<typeof getDocumentList>): SagaIterator {
  try {
    const [imageResults, otherAttachmentResults, commentOrReportsResults] = yield all([
      call(api.getImages, action.payload),
      call(api.getOtherAttachments, action.payload),
      call(api.getCommentOrReports, action.payload)
    ]);

    if (!imageResults.data || !otherAttachmentResults.data || !commentOrReportsResults.data) {
      yield put(getDocumentListFail());
    } else {
      const allResultsData = imageResults.data
        .concat(otherAttachmentResults.data)
        .concat(commentOrReportsResults.data);

      yield put(getDocumentListSuccess(allResultsData));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(getDocumentListFail());
    } else {
      yield put(getDocumentListFail());
    }
  }
}

function* handleRimStatusChange(action: ReturnType<typeof rimStatusChange>): SagaIterator {
  try {
    const result = yield call(rimApi.setStatus, action.payload.id, action.payload.rimStatusTC);

    if (!result.data) {
      yield put(rimStatusChangeFail(action.payload.id, action.payload.rimStatusTC));
    } else {
      yield put(rimStatusChangeSucess(action.payload.id, action.payload.rimStatusTC));
      const rimIds: number[] = yield select(selectors.rimIds);
      yield put(validateRimList(rimIds));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(rimStatusChangeFail(action.payload.id, action.payload.rimStatusTC));
    } else {
      yield put(rimStatusChangeFail(action.payload.id, action.payload.rimStatusTC));
    }
  }
}

function* handleGenerateRimReports(action: ReturnType<typeof generateRimReports>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const result = yield call(rimApi.generateReports, { ...action.payload });

    if (!result.data) {
      yield put(generateRimReportsFail(JSON.stringify(result.response)));
    } else {
      yield put(generateRimReportsSuccess(result.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(generateRimReportsFail(err));
    } else {
      yield put(generateRimReportsFail(err));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleGenerateSvsReports(action: ReturnType<typeof generateSvsReports>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const result = yield call(svsApi.generateReports, { ...action.payload });
    if (!result.data) {
      yield put(generateSvsReportsFail(JSON.stringify(result.response)));
    } else {
      yield put(generateSvsReportsSuccess(result.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(generateSvsReportsFail(err));
    } else {
      yield put(generateSvsReportsFail(err));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleGetAskedAmount(action: ReturnType<typeof getAskedAmount>): SagaIterator {
  try {
    const result = yield call(api.getAskedAmount, action.payload);

    if (!result.data) {
      yield put(getAskedAmountFail());
    } else {
      yield put(getAskedAmountSuccess(result.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(getAskedAmountFail());
    } else {
      yield put(getAskedAmountFail());
    }
  }
}

function* handleSetAskedAmount(action: ReturnType<typeof setAskedAmount>): SagaIterator {
  try {
    const result = yield call(api.setAskedAmount, action.payload.instructionId, action.payload.amount);

    if (!result.data) {
      yield put(setAskedAmountFail());
    } else {
      yield put(setAskedAmountSuccess(action.payload.amount));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(setAskedAmountFail());
    } else {
      yield put(setAskedAmountFail());
    }
  }
}


function* watchCommunicationOverview() {
  yield takeEvery(OverviewActionTypes.GET_RIM_LIST, handleGetRimList);
  yield takeEvery(OverviewActionTypes.GET_SVS_LIST, handleGetSvsList);
  yield takeEvery(OverviewActionTypes.GET_DOCUMENT_LIST, handleGetDocumentList);
  yield takeEvery(OverviewActionTypes.GET_HONORARY_NOTE_LIST, handleGetHonoraryNoteList);
  yield takeEvery(OverviewActionTypes.SEND_ITEMS, handleSendItems);
  yield takeEvery(OverviewActionTypes.SEND_ITEMS_WITH_WARNING, handleSendItemsWithWarning);
  yield takeEvery(OverviewActionTypes.DELETE_ITEMS, handleDeleteItems);
  yield takeEvery(OverviewActionTypes.SVS_VALIDATE_LIST, handleValidateSvsForSend);
  yield takeEvery(OverviewActionTypes.RIM_VALIDATE_LIST, handleValidateRimForSend);
  yield takeEvery(OverviewActionTypes.RIM_STATUS_CHANGE, handleRimStatusChange);
  yield takeEvery(OverviewActionTypes.GENERATE_REPORTS_RIM, handleGenerateRimReports);
  yield takeEvery(OverviewActionTypes.GET_ASKED_AMOUNT, handleGetAskedAmount);
  yield takeEvery(OverviewActionTypes.SET_ASKED_AMOUNT, handleSetAskedAmount);
  yield takeEvery(OverviewActionTypes.GENERATE_REPORTS_SVS, handleGenerateSvsReports);
}

function* communicationOverviewSaga() {
  yield all([fork(watchCommunicationOverview)]);
}

export default communicationOverviewSaga;
